import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { CONSENT } from '@/common/constants/common';
import { useGetStaffAllListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditConsentForm = forwardRef(({ type, setType, details }: Props, ref) => {
	const { t } = useTranslation();
	const defaultValues: DefaultValues<any> = {
		mailRequest: null,
	};
	const userInfo = useSelector(selectMyInfo);

	const { data: staffList }: any = useGetStaffAllListQuery(
		userInfo?.currentBranch?.id || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !userInfo?.currentBranch,
		}
	);

	useEffect(() => {
		if (details) methods.reset(details);
	}, [details]);

	const formSchema = Yup.object().shape({
		nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
		applicationSignatureDate: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		guardianSignatureDate: Yup.string()
			.nullable()
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		witnessId: Yup.string().required(t('errorMessage.pleaseEnter')),
		witnessPosition: Yup.string().required(t('errorMessage.pleaseEnter')),
		witnessSignatureDate: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		mailRequest: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	useImperativeHandle(ref, () => ({
		getFormData: () => methods.getValues(),
		validateForm: () => {
			methods.trigger();
			methods.formState.isValid;
			return methods.trigger().then(() => methods.formState.isValid);
		},
	}));

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.applicantGuardianInfo')}
					</Typography>
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={6}>
							<AppTextField
								control={methods.control}
								name='nameZh'
								label={t('preAdmission.applicantName') + '*'}
							/>
						</Grid>
						<Grid size={6}>
							<AppTextField
								control={methods.control}
								name='hkid'
								label={t('preAdmission.applicantHKID')}
							/>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								control={methods.control}
								name='applicationSignatureDate'
								label={t('preAdmission.applicationSignatureDate') + '*'}
							/>
						</Grid>
						<Grid size={6} />
						<Grid size={6}>
							<AppTextField
								control={methods.control}
								name='guardianName'
								label={t('preAdmission.parentGuardianName')}
							/>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								control={methods.control}
								name='guardianSignatureDate'
								label={t('preAdmission.parentGuardianSignatureDateConsent')}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />

					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.witnessInformation')}
					</Typography>
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={6}>
							<AppDropDownMenu
								control={methods.control}
								name='witnessId'
								label={t('preAdmission.witnessId') + '*'}
								options={staffList?.data || []}
							/>
						</Grid>
						<Grid size={6}>
							<AppTextField
								control={methods.control}
								name='witnessPosition'
								label={t('preAdmission.witnessPosition') + '*'}
							/>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								control={methods.control}
								name='witnessSignatureDate'
								label={t('preAdmission.witnessDate') + '*'}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />

					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.consentForm')}
					</Typography>
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={12}>
							<Typography variant='body2' color='common.neutral40'>
								{t('preAdmission.postalPreference') + '*'}
							</Typography>
							<AppRadioGroup
								control={methods.control}
								name='mailRequest'
								row
								options={CONSENT}
							/>
						</Grid>
					</Grid>
				</form>
			</FormProvider>
		</>
	);
});

EditConsentForm.displayName = 'EditConsentForm';

export default EditConsentForm;
